import {
  CATEGORY_L1_VALUES,
  CATEGORY_L2_VALUES,
  normalizeCategoryL1,
  normalizeCategoryL2,
  type CategoryL1,
  type CategoryL2,
} from '../server/categoryTaxonomy';

export type ResourceFacetRow = {
  category?: string | null;
  category_l1?: string | null;
  category_l2?: string | null;
  tags?: string[] | null;
};

export function buildPageWindow(params: { page: number; pageSize: number }): {
  page: number;
  pageSize: number;
  from: number;
  to: number;
} {
  const pageSize = Number.isFinite(params.pageSize) ? Math.max(1, Math.floor(params.pageSize)) : 40;
  const page = Number.isFinite(params.page) ? Math.max(1, Math.floor(params.page)) : 1;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  return { page, pageSize, from, to };
}

export function toPageSummary(params: { page: number; pageSize: number; total: number }): {
  currentPage: number;
  totalPages: number;
  pageStart: number;
  pageEnd: number;
} {
  const pageSize = Number.isFinite(params.pageSize) ? Math.max(1, Math.floor(params.pageSize)) : 40;
  const total = Number.isFinite(params.total) ? Math.max(0, Math.floor(params.total)) : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Number.isFinite(params.page)
    ? Math.min(totalPages, Math.max(1, Math.floor(params.page)))
    : 1;

  if (total <= 0) {
    return {
      currentPage,
      totalPages,
      pageStart: 0,
      pageEnd: 0,
    };
  }

  const pageStart = (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, total);
  return {
    currentPage,
    totalPages,
    pageStart,
    pageEnd,
  };
}

export function aggregateResourceFacets(params: {
  rows: ResourceFacetRow[];
  activeL1: 'all' | CategoryL1;
  activeL2: 'all' | CategoryL2;
  topTagsLimit?: number;
}): {
  l1Counts: Record<CategoryL1, number>;
  l2Counts: Record<CategoryL2, number>;
  availableTags: Array<[string, number]>;
} {
  const topTagsLimit = Number.isFinite(params.topTagsLimit)
    ? Math.max(1, Math.floor(params.topTagsLimit as number))
    : 24;
  const l1Counts = CATEGORY_L1_VALUES.reduce((acc, value) => {
    acc[value] = 0;
    return acc;
  }, {} as Record<CategoryL1, number>);
  const l2Counts = CATEGORY_L2_VALUES.reduce((acc, value) => {
    acc[value] = 0;
    return acc;
  }, {} as Record<CategoryL2, number>);
  const tagCounter = new Map<string, number>();

  for (const row of params.rows) {
    const l1 = normalizeCategoryL1(row.category_l1 || row.category || 'pending');
    const l2 = normalizeCategoryL2(row.category_l2 || 'development');
    l1Counts[l1] += 1;

    if (params.activeL1 === 'all' || l1 === params.activeL1) {
      l2Counts[l2] += 1;
    }

    if (params.activeL1 !== 'all' && l1 !== params.activeL1) continue;
    if (params.activeL2 !== 'all' && l2 !== params.activeL2) continue;

    for (const tag of row.tags || []) {
      const cleaned = tag.trim();
      if (!cleaned) continue;
      tagCounter.set(cleaned, (tagCounter.get(cleaned) || 0) + 1);
    }
  }

  const availableTags = Array.from(tagCounter.entries())
    .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
    .slice(0, topTagsLimit);

  return {
    l1Counts,
    l2Counts,
    availableTags,
  };
}
